/**
 * ============================================================================
 * Sessions — everything you can run, in one list
 * ============================================================================
 * The per-section list on the builder is where a session is scheduled. This is
 * the other door: an instructor arriving to teach does not want to remember
 * which section their class hangs off, they want to find tonight's class and
 * open it.
 *
 * Ordered soonest-first, because the one you need is almost always the next
 * one. The API returns newest-first for the section view; that is the wrong
 * order for someone standing in a room, so it is reversed here.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import { useAuth } from '../auth/useAuth.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import Field from '../components/Field.jsx';
import { SessionNextStep, SessionStageBadge } from '../components/SessionNextStep.jsx';

function when(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SessionsPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [rows, setRows] = useState(undefined);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  /** Path B (M28): start a class with no section. */
  const [impromptu, setImpromptu] = useState({ open: false, title: '', topic: '', location: '' });
  const canImpromptu = profile?.permissions?.includes('session.impromptu') ?? false;

  async function startImpromptu(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await apiFetch('/api/v1/sessions/impromptu', {
        method: 'POST',
        body: JSON.stringify({
          institution_id: profile?.institution?.id,
          title: impromptu.title.trim(),
          topic: impromptu.topic.trim() || null,
          location: impromptu.location.trim() || null,
        }),
      });
      // It starts now: open it, then go straight to the room.
      await apiFetch(`/api/v1/sessions/${created.session.id}/open`, { method: 'POST' });
      navigate(`/sessions/${created.session.id}/roster`);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  // The board (M37): what is on now and coming up, then what recently
  // closed. Cancelled and hidden sessions stay behind a toggle — a rehearsal
  // is history, not the day's work.
  const [showAll, setShowAll] = useState(false);
  const [showOlder, setShowOlder] = useState(false);

  const load = useCallback(async () => {
    try {
      // Two calls rather than one: the sessions endpoint returns the session's
      // own columns, and a room full of "class_id 9a7976ae…" would be useless.
      const [sessions, sections] = await Promise.all([
        apiFetch(showAll ? '/api/v1/sessions?include_hidden=1' : '/api/v1/sessions'),
        apiFetch('/api/v1/classes'),
      ]);
      const byId = new Map((sections ?? []).map((c) => [c.id, c]));
      const list = (sessions ?? []).map((s) => ({
        ...s,
        section_title: byId.get(s.class_id)?.title ?? null,
        section_archived: byId.get(s.class_id)?.status === 'archived',
      }));
      list.sort((a, b) => {
        // Anything open now floats to the top — that is the one being taught.
        if ((a.status === 'active') !== (b.status === 'active')) {
          return a.status === 'active' ? -1 : 1;
        }
        return String(a.scheduled_start).localeCompare(String(b.scheduled_start));
      });
      setRows(list);
    } catch (err) {
      setError(err);
      setRows(null);
    }
  }, [showAll]);

  useEffect(() => {
    load();
  }, [load]);

  async function setHidden(id, hidden) {
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/sessions/${id}/${hidden ? 'hide' : 'unhide'}`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function open(id) {
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/sessions/${id}/open`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <h1>Sessions</h1>
      <ErrorBanner error={error} />

      {canImpromptu && (
        <section className="panel" aria-labelledby="impromptu-heading">
          <h2 id="impromptu-heading">Start a class now</h2>
          <p className="muted">
            No section built for it yet? Give it a working title and go. The room checks in exactly as
            usual; a coordinator builds the section afterwards and binds this class to it, and only
            then can it be certified and awarded. This is how a tailgate runs.
          </p>
          {!impromptu.open ? (
            <button
              type="button"
              className="btn btn--auto"
              onClick={() => setImpromptu((i) => ({ ...i, open: true }))}
              disabled={busy}
            >
              Start an impromptu class
            </button>
          ) : (
            <form onSubmit={startImpromptu} className="impromptu">
              <Field
                label="Working title"
                value={impromptu.title}
                onChange={(v) => setImpromptu((i) => ({ ...i, title: v }))}
                hint="What you would write on the whiteboard. “Tailgate — vent practice.”"
                disabled={busy}
              />
              <div className="field-row">
                <Field
                  label="Topic"
                  value={impromptu.topic}
                  onChange={(v) => setImpromptu((i) => ({ ...i, topic: v }))}
                  required={false}
                  disabled={busy}
                />
                <Field
                  label="Where"
                  value={impromptu.location}
                  onChange={(v) => setImpromptu((i) => ({ ...i, location: v }))}
                  required={false}
                  hint="Bay 3, Station 12…"
                  disabled={busy}
                />
              </div>
              <p className="toolbar">
                <button type="submit" className="btn btn--primary btn--auto" disabled={busy || !impromptu.title.trim()}>
                  {busy ? 'Starting…' : 'Start the class and get the code'}
                </button>
                <button
                  type="button"
                  className="btn btn--quiet btn--auto"
                  onClick={() => setImpromptu({ open: false, title: '', topic: '', location: '' })}
                  disabled={busy}
                >
                  Cancel
                </button>
              </p>
            </form>
          )}
        </section>
      )}

      {(() => {
        if (rows === undefined) {
          return (
            <section className="panel">
              <p className="muted" aria-busy="true">
                Loading…
              </p>
            </section>
          );
        }
        if (!rows) return null;
        const live = rows.filter((r) => (r.status === 'active' || r.status === 'scheduled') && !r.section_archived);
        const cutoff = Date.now() - 60 * 86_400_000;
        const isRecent = (r) => Date.parse(r.scheduled_start) >= cutoff;
        const closed = rows.filter((r) => r.status === 'closed' && !r.hidden_at && !r.section_archived && isRecent(r));
        const older = rows.filter((r) => r.status === 'closed' && !r.hidden_at && !r.section_archived && !isRecent(r));
        const rest = rows.filter((r) => r.status === 'canceled' || r.hidden_at || r.section_archived);
        const hiddenCount = rest.length;

        const table = (list) => (
          <div className="schedule__scroll">
            <table className="schedule__table">
              <thead>
                <tr>
                  <th scope="col">When</th>
                  <th scope="col">Section</th>
                  <th scope="col">Where</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((s) => (
                  <tr key={s.id} style={s.hidden_at || s.section_archived ? { opacity: 0.6 } : undefined}>
                    <td>{when(s.scheduled_start)}</td>
                    <td>
                      {s.section_title ? (
                        <Link to={`/sections/${s.class_id}`}>{s.section_title}</Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                      {s.is_impromptu && !s.class_version && (
                        <>
                          <br />
                          <span className="badge badge--warn">no section yet</span>
                        </>
                      )}
                      {s.section_archived && (
                        <>
                          <br />
                          <span className="badge badge--archived">section archived</span>
                        </>
                      )}
                    </td>
                    <td>{s.location || <span className="muted">—</span>}</td>
                    <td>
                      <SessionStageBadge session={s} />
                      {s.hidden_at && <span className="badge"> off the board</span>}
                    </td>
                    <td>
                      <SessionNextStep session={s} onOpen={open} busy={busy}>
                        {(s.status === 'closed' || s.status === 'canceled') && !s.hidden_at && (
                          <button type="button" className="btn btn--quiet btn--auto" disabled={busy} onClick={() => setHidden(s.id, true)} title="Hides it from this list only. Records are untouched.">
                            Take off the board
                          </button>
                        )}
                        {s.hidden_at && (
                          <button type="button" className="btn btn--quiet btn--auto" disabled={busy} onClick={() => setHidden(s.id, false)}>
                            Put back
                          </button>
                        )}
                      </SessionNextStep>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

        return (
          <>
            <section className="panel" aria-labelledby="live-sessions">
              <h2 id="live-sessions">Now and coming up</h2>
              <p className="muted">
                Each row offers the next step. Open the class when it starts; <strong>Run it</strong> holds the check-in
                code, the roster, <strong>Present ▶</strong> and <strong>Close</strong>. Sessions are scheduled from the
                section they belong to.
              </p>
              {live.length === 0 ? (
                <p className="muted">
                  Nothing scheduled. Open a section from <Link to="/sections">Course sections</Link> and schedule one.
                </p>
              ) : (
                table(live)
              )}
            </section>

            <section className="panel" aria-labelledby="closed-sessions">
              <h2 id="closed-sessions">Recently closed</h2>
              <p className="muted">
                Each closed class shows what it still needs — certify, then award — and then its closing packet. A
                rehearsal you no longer want to see can be taken off the board; that hides it here and nowhere else.
              </p>
              {closed.length === 0 ? <p className="muted">Nothing closed yet.</p> : table([...closed].reverse())}
            </section>

            {older.length > 0 && (
              <section className="panel" aria-labelledby="older-sessions">
                <h2 id="older-sessions">
                  Earlier <span className="muted" style={{ fontWeight: 400, fontSize: '0.95rem' }}>· {older.length} closed more than 60 days ago</span>
                </h2>
                <button type="button" className="btn btn--quiet btn--auto" onClick={() => setShowOlder((v) => !v)}>
                  {showOlder ? 'Hide' : 'Show them'}
                </button>
                {showOlder && table(older)}
              </section>
            )}

            <section className="panel" aria-labelledby="hidden-sessions">
              <h2 id="hidden-sessions">
                Cancelled and off the board{' '}
                <span className="muted" style={{ fontWeight: 400, fontSize: '0.95rem' }}>
                  · {showAll ? hiddenCount : 'hidden'}
                </span>
              </h2>
              <button type="button" className="btn btn--quiet btn--auto" onClick={() => setShowAll((v) => !v)}>
                {showAll ? 'Hide them again' : 'Show them'}
              </button>
              {showAll && rest.length > 0 && table(rest)}
              {showAll && rest.length === 0 && <p className="muted">None.</p>}
            </section>
          </>
        );
      })()}
    </>
  );
}

export default SessionsPage;
